import React, { useCallback, useEffect, useState } from 'react';
import {
  VictoryChart,
  VictoryLine,
  VictoryAxis,
  VictoryScatter,
  VictoryLabel
} from 'victory';
import { assign } from 'lodash';
import { formatNumberWithPrefixAndMultiplier } from 'utils/format';

// Colors
const grey = '#D8D8D8';
const blue = '#0779AF';
const black = '#333333';

const panelGraphTheme = {
  axis: assign({
    style: {
      grid: {
        stroke: grey,
        strokeWidth: 1
      }
    }
  })
};

const CurvePanelGraph = ({
  curveData,
  xMultiplier,
  yMultiplier,
  xAxisLabel,
  yAxisLabel
}) => {
  const [graphXMultiplier, setGraphXMultiplier] = useState(0);
  const [graphYMultiplier, setGraphYMultiplier] = useState(0);
  const [virtualCurveData, setVirtualCurveData] = useState([]);

  const formatData = data => {
    const filteredCoordinates = data.filter(
      coord => !isNaN(coord.xvalue) && !isNaN(coord.yvalue)
    );
    const formattedData = filteredCoordinates.map(coord => {
      return {
        x: coord.xvalue,
        y: coord.yvalue
      };
    });

    return formattedData;
  };

  const renewGraph = useCallback(() => {
    const graphMultipliers = { x: 0, y: 0 };

    //Convert database values into their intended output
    const graphCurveData = curveData.map(point => {
      let { xvalue, yvalue } = point;
      xvalue = xvalue * Math.pow(10, xMultiplier);
      yvalue = yvalue * Math.pow(10, yMultiplier);
      return { ...point, xvalue, yvalue };
    });

    const virtualPointLeading = { excitation: 1, xvalue: 0, yvalue: 0 };
    const virtualPointTrailing = { excitation: 1, xvalue: 0, yvalue: 0 };

    //Sort by x value for virtual data without absolute value
    //We will use this to find the x values of the virtual graph data.
    const sortedXValuesGraphCurveData = graphCurveData.slice().sort((a, b) => {
      a = a['xvalue'];
      b = b['xvalue'];
      return b - a;
    });

    const biggestXValue = sortedXValuesGraphCurveData[0];
    const smallestXValue =
      sortedXValuesGraphCurveData[sortedXValuesGraphCurveData.length - 1];
    const difference = (biggestXValue['xvalue'] - smallestXValue['xvalue']) / 2;
    virtualPointLeading['xvalue'] = smallestXValue['xvalue'] - difference;
    virtualPointLeading['yvalue'] = graphCurveData[0]['yvalue'];
    virtualPointTrailing['xvalue'] = biggestXValue['xvalue'] + difference;
    virtualPointTrailing['yvalue'] =
      graphCurveData[graphCurveData.length - 1]['yvalue'];

    //Work with both axes
    ['x', 'y'].forEach(axis => {
      //Sort for highest absolute value
      //We will use the highest number to determine the multiplier to display on the graph
      const sortedGraphCurveData = graphCurveData.slice().sort((a, b) => {
        a = Math.abs(a[`${axis}value`]);
        b = Math.abs(b[`${axis}value`]);
        return b - a;
      });

      const highest = {};
      highest[`${axis}`] = sortedGraphCurveData[0][`${axis}value`];

      let highestInspect = sortedGraphCurveData[0][`${axis}value`];
      if (highestInspect === '') {
        highestInspect = 0;
      }

      //Find the multiplier to display on the graph axis.
      graphMultipliers[axis] =
        formatNumberWithPrefixAndMultiplier(highestInspect).multiplier;
    });

    const virtualCurveData = [
      virtualPointLeading,
      ...graphCurveData,
      virtualPointTrailing
    ];

    const { x: graphxMultiplier, y: graphyMultiplier } = graphMultipliers;

    setGraphXMultiplier(graphxMultiplier);
    setGraphYMultiplier(graphyMultiplier);
    setVirtualCurveData(virtualCurveData);
  }, [curveData, xMultiplier, yMultiplier]);

  useEffect(() => {
    renewGraph();
  }, [renewGraph]);

  /**
   * Determines amount of zeros at the beginning of a decimal
   * e.g. .0001 returns 3;
   */

  let xLabelConverted = formatNumberWithPrefixAndMultiplier(
    1 * Math.pow(10, graphXMultiplier)
  );
  let yLabelConverted = formatNumberWithPrefixAndMultiplier(
    1 * Math.pow(10, graphYMultiplier)
  );
  const xLabel = graphXMultiplier
    ? `(${xLabelConverted.prefix} - ${xLabelConverted.name})`
    : '';
  const yLabel = graphYMultiplier
    ? `(${yLabelConverted.prefix} - ${yLabelConverted.name})`
    : '';

  const filteredCoordinates = virtualCurveData.filter(
    coord =>
      !isNaN(coord.xvalue) &&
      coord.xvalue !== '' &&
      !isNaN(coord.yvalue) &&
      coord.yvalue !== ''
  );

  const formattedCurveData = formatData(filteredCoordinates);

  return (
    <VictoryChart theme={panelGraphTheme} height={350} padding={60}>
      <VictoryLabel
        text={xAxisLabel ? `${xAxisLabel} ${xLabel}` : `Duration (s) ${xLabel}`}
        y={340}
        x={250}
        textAnchor="middle"
      />
      <VictoryLabel
        text={
          yAxisLabel ? `${yAxisLabel} ${yLabel}` : `Frequency (Hz) ${yLabel}`
        }
        y={175}
        x={5}
        angle={-90}
        textAnchor="middle"
      />
      <VictoryAxis
        standalone={false}
        crossAxis
        tickCount={12}
        axis={-45}
        tickFormat={t => {
          if (Math.abs(t) > 999 * Math.pow(10, graphXMultiplier)) {
            return `${(t / 1000 / Math.pow(10, graphXMultiplier)).toFixed(2)}k`;
          }
          return (t / Math.pow(10, graphXMultiplier)).toFixed(2);
        }}
        style={{
          axis: {
            stroke: black
          },
          tickLabels: {
            angle: -45,
            padding: 10
          }
        }}
      />
      <VictoryAxis
        standalone={false}
        crossAxis
        dependentAxis
        style={{
          axis: {
            stroke: black
          },
          tickLabels: {
            angle: -45,
            padding: 10
          }
        }}
        tickCount={6}
        tickFormat={t => {
          t = parseFloat(t).toFixed(15);
          if (Math.abs(t) > 999 * Math.pow(10, graphYMultiplier)) {
            return `${(t / 1000 / Math.pow(10, graphYMultiplier)).toFixed(2)}k`;
          }
          return (t / Math.pow(10, graphYMultiplier)).toFixed(2);
        }}
      />
      <VictoryLine
        data={
          formattedCurveData.length !== 0
            ? formattedCurveData
            : [{ x: 0, y: 0 }]
        }
        style={{
          data: {
            strokeWidth: 3,
            strokeDasharray: 'none',
            stroke: blue
          }
        }}
        sortKey={0}
      />
      <VictoryScatter
        style={{ data: { fill: blue } }}
        size={3}
        data={
          formattedCurveData.length !== 0
            ? formattedCurveData
            : [{ x: 0, y: 0 }]
        }
      />
    </VictoryChart>
  );
};

export default CurvePanelGraph;
